import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useT } from "@/i18n/i18n";
import api from "@/lib/api";
import { DemoBadge, StatusBadge } from "@/components/shared";
import HoneyLabel from "@/components/HoneyLabel";
import { Hexagon } from "lucide-react";

export default function HoneyTrace() {
  const { batchId } = useParams();
  const { lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/public/honey-trace/${batchId}`).then((r) => setD(r.data)).catch(() => setErr(true)); }, [batchId]);

  if (err) return <div className="mx-auto max-w-2xl px-4 py-20 text-center text-mutedtext">{L("Nie znaleziono partii miodu.", "Honey batch not found.")}</div>;
  if (!d) return <div className="mx-auto max-w-2xl px-4 py-20 text-center text-mutedtext">…</div>;

  return (
    <div className="mx-auto max-w-3xl px-4 py-16">
      <div className="flex items-center gap-3">
        <Hexagon className="h-8 w-8" style={{ color: "#C98A1D" }} aria-hidden="true" />
        <div>
          <div className="font-mono text-sm text-mutedtext">{d.batch_id}</div>
          <h1 className="font-heading text-2xl font-bold text-charcoal">Miód Żywiącej Polski</h1>
        </div>
        {d.is_demo && <DemoBadge className="ml-auto" />}
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <Info label={L("Pochodzenie", "Origin status")} value={<OriginBadge status={d.origin_status} L={L} />} />
        <Info label={L("Polskie pochodzenie", "Polish origin")} value={d.polish_origin ? L("Tak", "Yes") : L("Nie", "No")} />
        <Info label={L("Producent / spółdzielnia", "Producer / cooperative")} value={d.producer_publishable ? d.producer : L("Nieopublikowano", "Not published")} />
        <Info label={L("Region", "Region")} value={<span className="capitalize">{d.region}</span>} />
        <Info label={L("Rok zbioru", "Harvest year")} value={d.harvest_year} />
        <Info label={L("Klasyfikacja", "Floral classification")} value={d.floral} />
        <Info label={L("Status badań", "Testing status")} value={<StatusBadge status={d.testing_status} />} />
      </div>
      {Object.keys(d.lab_summary || {}).length > 0 && (
        <div className="mt-6 rounded-md border border-subtle bg-white p-4">
          <div className="text-sm font-semibold text-charcoal">{L("Podsumowanie laboratoryjne", "Laboratory summary")}</div>
          <div className="mt-2 flex flex-wrap gap-3 text-sm text-mutedtext">
            {Object.entries(d.lab_summary).map(([k, v]) => <span key={k} className="rounded bg-secondary px-2 py-1">{k}: {String(v)}</span>)}
          </div>
          <p className="mt-2 text-xs text-mutedtext">{L("Przegląd w aplikacji nie jest certyfikatem laboratoryjnym.", "In-app review is not a laboratory certification.")}</p>
        </div>
      )}
      <p className="mt-6 rounded-md p-4 text-sm font-semibold" style={{ background: "#F6EAD0", color: "#8A5A12" }}>{d.warning}</p>
      <HoneyLabel data={d} batchId={batchId} />
    </div>
  );
}
const ORIGIN_TEXT = {
  verified_polish: { pl: "Zweryfikowane polskie pochodzenie", en: "Verified Polish origin" },
  producer_reported: { pl: "Zgłoszone przez producenta", en: "Producer-reported" },
  packed_in_poland: { pl: "Zapakowano w Polsce", en: "Packed in Poland" },
  pending: { pl: "Weryfikacja w toku", en: "Verification pending" },
};
function OriginBadge({ status, L }) {
  const o = ORIGIN_TEXT[status] || ORIGIN_TEXT.pending;
  return <span className="font-semibold" data-testid="trace-origin-status">{L(o.pl, o.en)}</span>;
}
function Info({ label, value }) {
  return <div className="rounded-md border border-subtle bg-white p-4"><div className="text-xs uppercase tracking-wide text-mutedtext">{label}</div><div className="mt-1 text-sm font-medium text-charcoal">{value}</div></div>;
}
